import React, { useCallback, useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Badge,
  Button,
  Col,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  InputGroup,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import FormLabel from "components/Extra/FormLabel"
import Select from "react-select"
import { FormInput } from "components/Extra/FormInput"
import { FormImageInput } from "components/Extra/FormImageInput"
import { useTranslation } from "react-i18next"
import { Product } from "pages/vendor/Products/Products"
import { createProduct, updateProduct, uploadProductImage, updateProductInventory, clearProductError } from "../../../../store/products/actions"
import { getAllProductCategories } from "../../../../store/product-categories/actions"
import { toast } from "react-toastify"

interface SelectOption {
  id: number | string
  name: string
}

interface AddNewProductProps {
  isOpen: boolean
  toggle: () => void
  product: Product | null
}

interface ImageValue {
  id: string
  file: File
  preview: string
  name: string
}

export const AddNewProduct: React.FC<AddNewProductProps> = ({
  isOpen,
  toggle,
  product,
}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const isEditMode = product !== null

  const [openFirstAccordion, setOpenFirstAccordion] = useState("0")
  const [openSecondAccordion, setOpenSecondAccordion] = useState("1")
  const [openThirdAccordion, setOpenThirdAccordion] = useState("0")
  const [openFourthAccordion, setOpenFourthAccordion] = useState("0")

  // Get categories from Redux for category dropdown
  const { categories } = useSelector((state: any) => state.ProductCategories)

  // Get product state from Redux for loading, success, and error handling
  const { loading, createSuccess, updateSuccess, error, createdProduct } = useSelector((state: any) => state.Products)

  // Store pending images for upload after product creation
  const [pendingImages, setPendingImages] = useState<ImageValue[]>([])

  const [selectedCategory, setSelectedCategory] = useState<{
    id: number
    name: string
  } | null>(null)

  const [selectedStockQuantity, setSelectedStockQuantity] = useState<{
    id: string
    name: string
  } | null>(null)

  // Handle form reset when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      if (product) {
        // Pre-fill category if editing
        setSelectedCategory({
          id: product.categoryId,
          name: product.categoryName || "",
        })
      } else {
        setSelectedCategory(null)
        setSelectedStockQuantity(null)
      }
    }
  }, [isOpen, product])

  const validation = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    initialValues: {
      // Backend fields (new contract)
      enName: product?.enName || "",
      arName: product?.arName || "",
      enDescription: product?.enDescription || "",
      arDescription: product?.arDescription || "",
      price: product?.price || 0,
      categoryId: product?.categoryId || null,
      categoryName: product?.categoryName || "",
      isActive: product?.isActive ?? true,
      availableQuantity: product?.totalAvailableQuantity || 0,
      brand: product?.brand || "",
      internalCode: product?.internalCode || "",
      stock: product?.stock || 0,
      lowStockThreshold: product?.lowStockThreshold || 0,
      isBundle: product?.isBundle || false,
      couponGenerationMode: product?.couponGenerationMode || "",
      unit: product?.unit || "",
      isPinned: product?.isPinned || false,
      pinnedOrder: product?.pinnedOrder || 0,
      specifications: product?.specifications || [
        {
          specNameEn: "",
          specNameAr: "",
          specValue: "",
          unit: "",
          displayOrder: 0,
          isHighlighted: false,
        },
      ],
      // Image fields
      productImages: [],
      barcode: null,
      // Inventory fields (for edit mode)
      terminalId: "",
      quantity: "",
      stockQuantity: null,
      // Non-backend fields from old design (kept for UI compatibility)
      latestTag: "",
      tags: [] as string[],
      phLevel: 0,
      soudiumLevel: 0,
      composition: [
        {
          name: "",
          value: 0,
        },
      ],
      productDescription: "",
      automaticCouponGeneration: true,
      ProductVisibility: true,
      pinProductVisibility: true,
      couponBasedProduct: true,
    },
    validationSchema: Yup.object({
      enName: Yup.string()
        .trim()
        .required(t("products_productNameValidationMessage") as string),
      arName: Yup.string()
        .trim()
        .required(t("products_productNameValidationMessage") as string),
      price: Yup.number()
        .min(0.01, t("products_minPriceValidationMessage") as string)
        .required(t("products_priceValidationMessage") as string),
      categoryId: Yup.number()
        .required(t("products_categoryValidationMessage") as string)
        .nullable(),
      availableQuantity: Yup.number()
        .min(0, t("products_minStockValidationMessage") as string)
        .required(t("products_stockQuantityValidationMessage") as string),
    }),
    onSubmit: values => {
      // Build product data according to new backend contract
      const productData: any = {
        enName: values.enName,
        arName: values.arName,
        price: values.price,
        categoryId: values.categoryId,
        isActive: values.isActive,
        availableQuantity: values.availableQuantity,
      }

      // Add optional fields only if they have values
      if (values.enDescription) productData.enDescription = values.enDescription
      if (values.arDescription) productData.arDescription = values.arDescription
      if (values.brand) productData.brand = values.brand
      if (values.internalCode) productData.internalCode = values.internalCode
      if (values.stock) productData.stock = values.stock
      if (values.lowStockThreshold) productData.lowStockThreshold = values.lowStockThreshold
      if (values.isBundle) productData.isBundle = values.isBundle
      if (values.couponGenerationMode) productData.couponGenerationMode = values.couponGenerationMode
      if (values.unit) productData.unit = values.unit
      if (values.isPinned) productData.isPinned = values.isPinned
      if (values.pinnedOrder) productData.pinnedOrder = values.pinnedOrder

      // Add specifications if they have valid data
      const validSpecs = values.specifications?.filter(
        (spec: any) => spec.specNameEn || spec.specNameAr || spec.specValue
      )
      if (validSpecs && validSpecs.length > 0) {
        productData.specifications = validSpecs
      }

      const productImages = values.productImages as ImageValue[]

      if (isEditMode && product) {
        // Update product
        dispatch(updateProduct(product.id, productData, { push: () => {} }))

        // Upload images if provided
        productImages.forEach((img, index) => {
          if (img.file) {
            const reader = new FileReader()
            reader.onloadend = () => {
              dispatch(uploadProductImage(product.id, {
                base64Image: reader.result as string,
                caption: img.name || `Image ${index + 1}`,
                isMain: index === 0,
              }, { push: () => {} }))
            }
            reader.readAsDataURL(img.file)
          }
        })
      } else {
        // Create product - store pending images for upload after creation
        setPendingImages(productImages)
        dispatch(createProduct(productData, { push: () => {} }))
      }
    },
  })

  // Handle success states - close modal and reset form only on success (no error)
  useEffect(() => {
    // Only close if there's a success flag AND no error
    if ((createSuccess || updateSuccess) && !error) {
      // For newly created products, upload pending images
      if (createSuccess && createdProduct && !isEditMode) {
        console.log("Created product:", createdProduct)
        // Backend returns nested structure: { result: null, value: { id: ... } }
        const productValue = createdProduct.value || createdProduct
        const productId = productValue?.id || productValue?.vsId
        console.log("Product ID for image upload:", productId)
        console.log("Pending images:", pendingImages)

        // Upload images if pending
        pendingImages.forEach((img, index) => {
          if (img.file && productId) {
            console.log(`Uploading image ${index + 1}...`)
            const reader = new FileReader()
            reader.onloadend = () => {
              const base64Data = reader.result as string
              console.log(`Image ${index + 1} base64 length:`, base64Data.length)
              dispatch(uploadProductImage(productId, {
                base64Image: base64Data,
                caption: img.name || `Image ${index + 1}`,
                isMain: index === 0,
              }, { push: () => {} }))
            }
            reader.readAsDataURL(img.file)
          }
        })

        // Clear pending images
        setPendingImages([])
      }

      // Close modal on success after a short delay to allow uploads to start
      setTimeout(() => {
        toggle()
        // Reset form after modal closes
        setTimeout(() => {
          validation.resetForm()
          setSelectedCategory(null)
          setSelectedStockQuantity(null)
        }, 300)
      }, 500)
      // Clear success flags after delay
      setTimeout(() => {
        dispatch(clearProductError())
      }, 1000)
    }
  }, [createSuccess, updateSuccess, error, createdProduct, pendingImages])

  // Handle error states - show error toast (already handled by saga)
  useEffect(() => {
    if (error) {
      // Error toast is already shown by saga
      // Keep the modal open so user can fix the error
      // Just clear the error state after showing
      setTimeout(() => {
        dispatch(clearProductError())
      }, 100)
    }
  }, [error])

  useEffect(() => {
    if (!isOpen) {
      validation.resetForm()
      setPendingImages([])
    }
  }, [isOpen])

  // Load categories on mount if not loaded
  useEffect(() => {
    if (isOpen && (!categories || categories.length === 0)) {
      dispatch(getAllProductCategories({ push: () => {} }))
    }
  }, [isOpen, categories, dispatch])

  const addNewComposition = useCallback(() => {
    validation.setValues({
      ...validation.values,
      composition: [
        ...validation.values.composition,
        {
          name: "",
          value: 0,
        },
      ],
    })
  }, [validation])

  const changeImageHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "productImages" | "barcode"
  ) => {
    if (!e.target.files || e.target.files.length === 0) return

    if (type === "productImages") {
      // Handle multiple files for product images
      const newImages = Array.from(e.target.files).map(file => ({
        id: `${Date.now()}-${Math.random()}`,
        file,
        preview: URL.createObjectURL(file),
        name: file.name,
      })) as ImageValue[]

      // Add all new images to the array
      validation.setFieldValue("productImages", [...validation.values.productImages, ...newImages])
    } else {
      // For barcode, only take the first file
      const file = e.target.files[0]
      const newImage = {
        id: `${Date.now()}`,
        file,
        preview: URL.createObjectURL(file),
        name: file.name,
      } as ImageValue
      validation.setFieldValue("barcode", newImage)
    }
  }

  const removeProductImageHandler = useCallback((index: number) => {
    const updatedImages = validation.values.productImages.filter((_: any, i: number) => i !== index)
    validation.setFieldValue("productImages", updatedImages)
  }, [validation])

  const removeBarcodeImageHandler = useCallback(() => {
    validation.setFieldValue("barcode", null)
  }, [validation])

  const addNewCategory = useCallback(() => {
    console.log("addNewCategory")
  }, [])

  const addTag = useCallback(() => {
    const trimmed = validation.values.latestTag.trim()
    validation.setFieldValue("tags", [...validation.values.tags, trimmed])
    validation.setFieldValue("latestTag", "")
  }, [validation])

  const removeTag = useCallback((tagToRemove: string) => {
    const tags = validation.values.tags.filter(tag => tag !== tagToRemove)
    validation.setFieldValue("tags", tags)
  }, [])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault()
      addTag()
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle} tag="h3">
        <h4 className="text-capitalize fw-semibold font-size-24">
          {isEditMode ? t("common_edit") : t("products_addNewProduct")}
        </h4>
      </ModalHeader>
      <ModalBody>
        <Form
          onSubmit={e => {
            e.preventDefault()
            validation.handleSubmit()
            return false
          }}
        >
          <Accordion open={openFirstAccordion}>
            <AccordionItem>
              <AccordionHeader
                targetId="0"
                onClick={() =>
                  setOpenFirstAccordion(openFirstAccordion === "0" ? "1" : "0")
                }
              >
                {t("common_basicInfo")}
              </AccordionHeader>
              <AccordionBody accordionId="0">
                <Row>
                  <Col md="6">
                    <FormInput
                      label={t("products_englishName") as string}
                      name="enName"
                      type="text"
                      placeholder={
                        t("products_enterProductNameEnglish") as string
                      }
                      validation={validation}
                      required
                    />
                  </Col>
                  <Col md="6">
                    <FormInput
                      label={t("products_arabicName") as string}
                      name="arName"
                      type="text"
                      placeholder={
                        t("products_enterProductNameArabic") as string
                      }
                      validation={validation}
                      required
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md="12">
                    <FormInput
                      label={t("products_englishDescription") as string}
                      name="enDescription"
                      type="textarea"
                      rows={2}
                      placeholder={
                        t("products_enterDescriptionEnglish") as string
                      }
                      validation={validation}
                    />
                  </Col>
                  <Col md="12">
                    <FormInput
                      label={t("products_arabicDescription") as string}
                      name="arDescription"
                      type="textarea"
                      rows={2}
                      placeholder={
                        t("products_enterDescriptionArabic") as string
                      }
                      validation={validation}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md="6">
                    <div className="d-flex align-items-end justify-content-start">
                      <div className="flex-grow-1 me-2">
                        <FormGroup>
                          <FormLabel htmlFor="categoryId" required>
                            {t("products_category")}
                          </FormLabel>
                          <Select
                            id="categoryId"
                            name="categoryId"
                            value={selectedCategory}
                            onChange={(option: any) => {
                              setSelectedCategory(option)
                              validation.setFieldValue(
                                "categoryId",
                                option?.id || null
                              )
                              validation.setFieldValue(
                                "categoryName",
                                option?.name || ""
                              )
                            }}
                            options={
                              categories?.map((cat: any) => ({
                                id: cat.id,
                                name: cat.eN_NAME,
                              })) || []
                            }
                            getOptionLabel={(option: any) => option.name}
                            getOptionValue={(option: any) => option.id}
                            classNamePrefix="select2-selection"
                            placeholder={t("products_selectCategory") as string}
                            isClearable
                            isSearchable
                          />
                          {validation.touched.categoryId &&
                            validation.errors.categoryId && (
                              <FormFeedback className="d-block">
                                {validation.errors.categoryId as string}
                              </FormFeedback>
                            )}
                        </FormGroup>
                      </div>
                      <span
                        className="input-group-append mb-3"
                        style={{ cursor: "pointer" }}
                        onClick={addNewCategory}
                      >
                        <i className="bx bx-plus font-size-18 p-2 px-2 input-group-text"></i>
                      </span>
                    </div>
                  </Col>
                  <Col md="6">
                    <FormInput
                      label={t("common_price") as string}
                      lightLabel={t("products_qarCurrency") as string}
                      name="price"
                      type="number"
                      step="0.01"
                      placeholder={t("products_enterProductPrice") as string}
                      validation={validation}
                      required
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md="6">
                    <FormInput
                      label={t("products_brand") as string}
                      name="brand"
                      type="text"
                      placeholder={t("products_enterBrand") as string}
                      validation={validation}
                      info={t("products_brandInfo") as string}
                    />
                  </Col>
                </Row>

                {/* Product Images Section - Using old FormImageInput style */}
                <div className="mb-3">
                  <FormLabel className="form-label">
                    {t("products_productImage")}
                  </FormLabel>

                  {/* Upload Button - Same style as FormImageInput */}
                  <div
                    className="d-flex justify-content-between align-items-center"
                    style={{ cursor: "pointer" }}
                    onClick={() =>
                      document.getElementById("productImageInput")?.click()
                    }
                  >
                    <InputGroup>
                      <input
                        type="text"
                        className="form-control me-2"
                        placeholder={t("products_productImage") as string}
                        readOnly
                        value={
                          validation.values.productImages?.length > 0
                            ? `${validation.values.productImages.length} ${
                                t("products_productImage") ||
                                "image(s) selected"
                              }`
                            : ""
                        }
                      />
                      <Input
                        type="file"
                        accept="image/*"
                        multiple
                        id="productImageInput"
                        style={{ display: "none" }}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          changeImageHandler(e, "productImages")
                        }
                      />
                      <span className="input-group-append">
                        <i className="mdi mdi-camera font-size-20 input-group-text"></i>
                      </span>
                    </InputGroup>
                  </div>

                  {/* Info text */}
                  {t("products_productImageInfo") && (
                    <div className="d-flex align-items-center gap-1 ms-2 mt-1">
                      <i className="bx bx-info-circle"></i>
                      <span className="text-muted small">
                        {t("products_productImageInfo") as string}
                      </span>
                    </div>
                  )}

                  {/* Display uploaded images - Using FormImageInput preview style */}
                  {validation.values.productImages &&
                    validation.values.productImages.length > 0 && (
                      <div className="d-flex gap-2 mt-2 flex-wrap">
                        {validation.values.productImages.map(
                          (img: ImageValue, index: number) => (
                            <div
                              key={img.id}
                              className="position-relative"
                              style={{
                                width: "80px",
                                height: "80px",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                overflow: "hidden",
                              }}
                            >
                              <img
                                src={img.preview}
                                alt={img.name}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                              <Button
                                close
                                onClick={() => removeProductImageHandler(index)}
                                style={{
                                  position: "absolute",
                                  top: "2px",
                                  right: "2px",
                                  background: "white",
                                  borderRadius: "50%",
                                  padding: "0",
                                  width: "20px",
                                  height: "20px",
                                }}
                              >
                                <i className="mdi mdi-close"></i>
                              </Button>
                            </div>
                          )
                        )}
                      </div>
                    )}
                </div>

                <div>
                  <FormInput
                    label={t("products_tag") as string}
                    name="latestTag"
                    type="text"
                    placeholder={t("products_enterTag") as string}
                    validation={validation}
                    info={t("products_tagInfo") as string}
                    onKeyDown={handleKeyDown}
                    disabled
                  />
                  <div className="mt-2">
                    {validation.values.tags.map((tag, index) => (
                      <Badge
                        key={index}
                        color="primary"
                        pill
                        className="me-1 px-3 py-2 font-size-12"
                        style={{ cursor: "pointer" }}
                        onClick={() => removeTag(tag)}
                      >
                        {tag} &times;
                      </Badge>
                    ))}
                  </div>
                </div>
                <FormGroup className="d-flex align-items-center gap-3">
                  <FormLabel htmlFor="couponBasedProduct">
                    <span className="fw-bold">
                      {t("products_couponBasedProduct")}
                    </span>{" "}
                    ({t("products_couponBasedProductInfo")})
                  </FormLabel>
                  <div className="form-check form-switch mb-2">
                    <input
                      name="couponBasedProduct"
                      type="checkbox"
                      className="form-check-input"
                      id="couponBasedProduct"
                      checked={validation.values.couponBasedProduct}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      disabled
                    />
                  </div>
                </FormGroup>
              </AccordionBody>
            </AccordionItem>
          </Accordion>

          <Accordion open={openSecondAccordion} className="mt-4">
            <AccordionItem>
              <AccordionHeader
                targetId="1"
                onClick={() =>
                  setOpenSecondAccordion(
                    openSecondAccordion === "1" ? "2" : "1"
                  )
                }
              >
                {t("products_inventory")}
              </AccordionHeader>
              <AccordionBody accordionId="1">
                <FormImageInput
                  name="barcode"
                  validation={validation}
                  changeImageHandler={e => changeImageHandler(e, "barcode")}
                  label="Internal Item code/Bar-code"
                  removeHandler={removeBarcodeImageHandler}
                  icon={
                    <i className="mdi mdi-barcode-scan font-size-20 input-group-text"></i>
                  }
                />
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <FormLabel htmlFor="stockQuantity" required>
                        {t("products_stockQuantity")}
                      </FormLabel>
                      <Select
                        id="stockQuantity"
                        value={selectedStockQuantity}
                        isDisabled={true}
                        onChange={(
                          selectedOption: { id: string; name: string } | null
                        ) => {
                          setSelectedStockQuantity(selectedOption)
                          validation.setFieldValue("stockQuantity", {
                            id: selectedOption?.id,
                            name: selectedOption?.name,
                          })
                        }}
                        options={Array.from(
                          { length: 10 },
                          (_, i) => i + 1
                        ).map(one => ({
                          id: one.toString(),
                          name: one.toString(),
                        }))}
                        getOptionLabel={(option: any) => option.name}
                        getOptionValue={(option: any) => option.id}
                        className="select2-selection"
                      />
                      {validation.touched.stockQuantity &&
                      validation.errors.stockQuantity ? (
                        <FormFeedback type="invalid">
                          {validation.errors.stockQuantity}
                        </FormFeedback>
                      ) : null}

                      <div className="d-flex align-items-center gap-1 ms-2">
                        <i className="bx bx-info-circle"></i>
                        <FormFeedback
                          type="invalid"
                          className="d-block text-secondary"
                        >
                          {t("products_availableStockForProduct") as string}
                        </FormFeedback>
                      </div>
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormInput
                      label={t("products_lowStock") as string}
                      name="lowStockThreshold"
                      type="number"
                      validation={validation}
                      placeholder={t("products_enterLowStock") as string}
                      info={t("products_lowStockInfo") as string}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md="6">
                    <FormInput
                      label={t("products_internalCode") as string}
                      name="internalCode"
                      type="text"
                      placeholder={t("products_enterInternalCode") as string}
                      validation={validation}
                      info={t("products_internalCodeInfo") as string}
                    />
                  </Col>
                  <Col md="6">
                    <FormInput
                      label={t("products_stock") as string}
                      name="stock"
                      type="number"
                      validation={validation}
                      placeholder={t("products_enterStock") as string}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md="6">
                    <FormInput
                      label={t("products_unit") as string}
                      name="unit"
                      type="text"
                      placeholder={t("products_enterUnit") as string}
                      validation={validation}
                      info={t("products_unitInfo") as string}
                    />
                  </Col>
                  <Col md="6">
                    <FormInput
                      label={t("products_pinnedOrder") as string}
                      name="pinnedOrder"
                      type="number"
                      validation={validation}
                      placeholder={t("products_enterPinnedOrder") as string}
                      info={t("products_pinnedOrderInfo") as string}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md="6">
                    <FormInput
                      label={t("products_terminal") as string}
                      name="terminalId"
                      type="number"
                      placeholder={t("products_enterTerminal") as string}
                      validation={validation}
                    />
                  </Col>
                  <Col md="6">
                    <FormInput
                      label={t("products_quantity") as string}
                      name="quantity"
                      type="number"
                      placeholder={t("products_enterQuantity") as string}
                      validation={validation}
                    />
                  </Col>
                </Row>
              </AccordionBody>
            </AccordionItem>
          </Accordion>

          <Accordion open={openThirdAccordion} className="mt-4">
            <AccordionItem>
              <AccordionHeader
                targetId="0"
                onClick={() =>
                  setOpenThirdAccordion(openThirdAccordion === "0" ? "1" : "0")
                }
              >
                {t("products_productDetails")}
              </AccordionHeader>
              <AccordionBody accordionId="0">
                <Row>
                  <Col md="6">
                    <FormInput
                      label={t("products_PH") as string}
                      name="phLevel"
                      type="number"
                      validation={validation}
                      placeholder={t("products_enterPH") as string}
                      lightLabel={t("products_MgL") as string}
                      disabled
                    />
                  </Col>
                  <Col md="6">
                    <div className="d-flex align-items-end justify-content-start">
                      <div className="flex-grow-1 me-2">
                        <FormInput
                          label={t("products_sodiumlevel") as string}
                          name="soudiumLevel"
                          type="number"
                          validation={validation}
                          placeholder={t("products_enterSodium") as string}
                          lightLabel={t("products_MgL") as string}
                          disabled
                        />
                      </div>
                      <span
                        className="input-group-append mb-3"
                        style={{ cursor: "not-allowed", opacity: "0.5" }}
                      >
                        <i
                          className="bx bx-plus font-size-18 p-1 px-2 input-group-text"
                          style={{ height: "45px" }}
                        ></i>
                      </span>
                    </div>
                  </Col>
                </Row>
                {validation.values.composition.map((_, index) => (
                  <Row key={index}>
                    <Col md="6">
                      <FormInput
                        label={t("products_compositionName") as string}
                        name={`composition[${index}].name`}
                        type="text"
                        validation={validation}
                        placeholder={
                          t("products_enterCompositionName") as string
                        }
                        disabled
                      />
                    </Col>
                    <Col md="6">
                      <FormInput
                        label={t("products_compositionValue") as string}
                        name={`composition[${index}].value`}
                        type="number"
                        validation={validation}
                        placeholder={
                          t("products_enterCompositionValue") as string
                        }
                        lightLabel={t("products_MgL") as string}
                        disabled
                      />
                    </Col>
                  </Row>
                ))}
                <FormInput
                  label={t("products_productDescription") as string}
                  name="productDescription"
                  type="textarea"
                  rows={3}
                  validation={validation}
                  placeholder={t("products_enterProductDescription") as string}
                  disabled
                />
              </AccordionBody>
            </AccordionItem>
          </Accordion>

          <Accordion open={openFourthAccordion} className="mt-4">
            <AccordionItem>
              <AccordionHeader
                targetId="2"
                onClick={() =>
                  setOpenFourthAccordion(
                    openFourthAccordion === "2" ? "3" : "2"
                  )
                }
              >
                {t("products_visibility")}
              </AccordionHeader>
              <AccordionBody accordionId="2">
                <FormGroup className="d-flex align-items-center gap-3">
                  <FormLabel htmlFor="automaticCouponGeneration">
                    <span className="fw-bold">
                      {t("products_couponGeneration")}
                    </span>{" "}
                    ({t("products_couponGenerationInfo")})
                  </FormLabel>
                  <div className="form-check form-switch mb-2">
                    <input
                      name="automaticCouponGeneration"
                      type="checkbox"
                      className="form-check-input"
                      id="automaticCouponGeneration"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      checked={validation.values.automaticCouponGeneration}
                      disabled
                    />
                  </div>
                </FormGroup>
                <FormGroup className="d-flex align-items-center gap-3">
                  <FormLabel htmlFor="isActive">
                    <span className="fw-bold">
                      {t("products_productVisibility")}
                    </span>{" "}
                    ({t("products_productVisibilityInfo")})
                  </FormLabel>
                  <div className="form-check form-switch mb-2">
                    <input
                      name="isActive"
                      type="checkbox"
                      className="form-check-input"
                      id="isActive"
                      checked={validation.values.isActive}
                      onClick={e => {
                        e.stopPropagation()
                        validation.setFieldValue(
                          "isActive",
                          !validation.values.isActive
                        )
                      }}
                      onChange={e => {
                        e.stopPropagation()
                      }}
                      onBlur={validation.handleBlur}
                    />
                  </div>
                </FormGroup>
                <FormGroup className="d-flex align-items-center gap-3">
                  <FormLabel htmlFor="isPinned">
                    <span className="fw-bold">{t("products_pinProduct")}</span>{" "}
                    ({t("products_pinProductInfo")})
                  </FormLabel>
                  <div className="form-check form-switch mb-2">
                    <input
                      name="isPinned"
                      type="checkbox"
                      className="form-check-input"
                      id="isPinned"
                      checked={validation.values.isPinned}
                      onClick={e => {
                        e.stopPropagation()
                        validation.setFieldValue(
                          "isPinned",
                          !validation.values.isPinned
                        )
                      }}
                      onChange={e => {
                        e.stopPropagation()
                      }}
                      onBlur={validation.handleBlur}
                    />
                  </div>
                </FormGroup>
                <FormGroup className="d-flex align-items-center gap-3">
                  <FormLabel htmlFor="isBundle">
                    <span className="fw-bold">{t("products_isBundle")}</span> (
                    {t("products_isBundleInfo")})
                  </FormLabel>
                  <div className="form-check form-switch mb-2">
                    <input
                      name="isBundle"
                      type="checkbox"
                      className="form-check-input"
                      id="isBundle"
                      checked={validation.values.isBundle}
                      onClick={e => {
                        e.stopPropagation()
                        validation.setFieldValue(
                          "isBundle",
                          !validation.values.isBundle
                        )
                      }}
                      onChange={e => {
                        e.stopPropagation()
                      }}
                      onBlur={validation.handleBlur}
                    />
                  </div>
                </FormGroup>
              </AccordionBody>
            </AccordionItem>
          </Accordion>

          <Row>
            <Col>
              <div className="d-flex justify-content-end gap-2 mt-4">
                <button
                  type="submit"
                  className={`btn btn-primary save-customer`}
                  disabled={
                    loading || !(validation.isValid && validation.dirty)
                  }
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      {isEditMode ? t("common_saving") : t("common_creating")}
                    </>
                  ) : (
                    <>
                      {isEditMode
                        ? t("common_save")
                        : t("products_createProduct")}
                    </>
                  )}
                </button>
                <button
                  onClick={toggle}
                  type="button"
                  className="btn btn-cancel text-primary border border-primary"
                >
                  {t("common_cancel")}
                </button>
              </div>
            </Col>
          </Row>
        </Form>
      </ModalBody>
    </Modal>
  )
}
